import React, { useState, useEffect } from "react";

import { Avatar, CardHeader, Typography } from "@material-ui/core";
import ContactStatusAvatar from "../ContactStatusAvatar";
import InactiveConnectionBadge from "../InactiveConnectionBadge";
import { isInactiveTicket } from "../../helpers/canAccessTicket";
import { describePresence, PRESENCE_HIGHLIGHT_COLOR } from "../../helpers/contactWhatsappStatus";

import { i18n } from "../../translate/i18n";

// Qual cliente "é" o contato: o vínculo principal, senão o primeiro.
// Fonte única desta regra — quem precisa do objeto (ex: pré-seleção ao criar
// card no Kanban) usa esta função; quem só precisa do texto usa getClienteLabel.
export const getPrimaryCliente = contact => {
	const links = contact?.contactClientes || [];
	if (links.length === 0) return null;

	const sorted = [...links].sort(
		(a, b) => (b.principal === true) - (a.principal === true)
	);
	return sorted[0]?.cliente || null;
};

// Deriva "NOMEFANTASIA - NOMECLI" do cliente vinculado ao contato
export const getClienteLabel = contact => {
	const cliente = getPrimaryCliente(contact);
	if (!cliente) return "";

	return [cliente.NOMEFANTASIA, cliente.NOMECLI]
		.filter(Boolean)
		.join(" - ");
};

const TicketInfo = ({ contact, ticket, onClick, whatsappStatus }) => {
	const { user } = ticket || {};
	const [userName, setUserName] = useState('')

	// Valor derivado, não estado: antes isto vivia num useEffect que cortava o
	// nome em 10 caracteres quando document.body.offsetWidth < 600. Aquilo
	// estava quebrado por três motivos — media a VIEWPORT (e o que aperta é o
	// painel de conversa, que fica estreito mesmo em janela larga), não
	// reexecutava no resize (deps [ticket, user, contact]) e cravava o "..."
	// dentro da própria string, o que impedia tanto o text-overflow do CSS
	// quanto qualquer tooltip de mostrar o nome inteiro. Agora quem trunca é o
	// CSS, via min-width:0 no cabeçalho (TicketHeader/compactStyles).
	const contactName = contact
		? contact.name ||
			(contact.instagramUsername ? `@${contact.instagramUsername}` : "") ||
			contact.number ||
			""
		: "";

	useEffect(() => {
		if (user && contact) {
			setUserName(`${i18n.t("messagesList.header.assignedTo")} ${user.name}`);

			if(document.body.offsetWidth < 600) {
				setUserName(`${user.name}`);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticket, ticket?.user, contact])

	// Não renderiza se não tiver dados mínimos necessários
	if (!ticket || !ticket.id || !contact || !contact.id) {
		return null;
	}

	const clienteLabel = getClienteLabel(contact);
	const presenceInfo = describePresence(whatsappStatus?.presence);

	return (
		<CardHeader
			onClick={onClick}
			style={{ cursor: "pointer" }}
			titleTypographyProps={{ noWrap: true }}
			subheaderTypographyProps={{ noWrap: true }}
			avatar={
				<ContactStatusAvatar
					contact={contact}
					stories={whatsappStatus?.stories}
					ticketId={ticket.id}
				/>
			}
			// O "#id" fica FORA do texto que ganha reticências. Como título era
			// a string única `${contactName} #${ticket.id}`, o número do ticket
			// — que é como as pessoas se referem ao atendimento — seria a
			// primeira coisa a sumir assim que o nome passasse a truncar.
			title={
				<span style={{ display: "flex", minWidth: 0, alignItems: "baseline" }}>
					<span
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							minWidth: 0,
						}}
						title={contactName}
					>
						{contactName}
					</span>
					<span style={{ flex: "none", paddingLeft: 4 }}>#{ticket.id}</span>
					{isInactiveTicket(ticket) && (
						<span style={{ flex: "none", paddingLeft: 6, alignSelf: "center", display: "inline-flex" }}>
							<InactiveConnectionBadge ticket={ticket} />
						</span>
					)}
				</span>
			}
			subheader={
				<>
					{clienteLabel && (
						<Typography
							variant="caption"
							display="block"
							noWrap
							color="textSecondary"
							title={clienteLabel}
						>
							{clienteLabel}
						</Typography>
					)}
					{/* Presença na MESMA linha do "Atribuído a", para o cabeçalho
					    compacto não ganhar altura. Vem primeiro: é a parte que
					    muda e não pode sumir nas reticências. */}
					{presenceInfo && (
						<span style={presenceInfo.highlight ? { color: PRESENCE_HIGHLIGHT_COLOR } : undefined}>
							{presenceInfo.text}
						</span>
					)}
					{presenceInfo && ticket.user && " · "}
					{ticket.user && `${userName}`}
				</>
			}
		/>
	);
};

export default TicketInfo;
